// features/Workspaces/WorkspaceContext.tsx
import {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { normalizeCode } from "./deriveWorkspaces";

export type Workspace = { code: string | null; label: string; group?: string };

type SetCurrentOptions = { source?: "ui" | "url" };

type Ctx = {
  current: Workspace | null;
  setCurrent: (ws: Workspace, opts?: SetCurrentOptions) => void;
  available: Workspace[];
  setAvailable: (list: Workspace[]) => void;
  uiSwitchTick: boolean;
};

const WorkspaceContext = createContext<Ctx | null>(null);

// A simple, safe default: only "All" and selected as current
export function WorkspaceProvider({
  children,
  initialAvailable = [],
}: {
  children: ReactNode;
  initialAvailable?: Workspace[];
}) {
  const [available, setAvailable] = useState<Workspace[]>(initialAvailable);
  const [current, setCurrentState] = useState<Workspace | null>(null);
  const [uiSwitchTick, setUiSwitchTick] = useState(false);

  const setCurrent = useCallback((w: Workspace, opts?: SetCurrentOptions) => {
    const source = opts?.source ?? "ui";

    // If user changed it, skip the *next* URL-driven sync once
    setUiSwitchTick(source === "ui");
    setCurrentState(w);
  }, []);

  useEffect(() => {
    if (!available.length) return;
    const findByCode = (code: string | null | undefined) =>
      available.find(
        (a) => normalizeCode(a.code) === (normalizeCode(code) ?? null),
      );

    if (!current || !findByCode(current.code)) {
      setCurrent(available[0], { source: "url" });
    }
  }, [current, available]);

  const value = useMemo(
    () => ({ current, setCurrent, available, setAvailable, uiSwitchTick }),
    [current, available],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx)
    throw new Error("useWorkspace must be used within <WorkspaceProvider>");
  return ctx;
}
